import type { ReportErrorOptions } from '../types'
import { createConsoleProvider } from './console-provider'
import { createNewRelicProvider } from './newrelic-provider'
import { createNoopProvider } from './noop-provider'
import type { MonitoringConfig, MonitoringProvider } from './types'

/**
 * Creates a composite provider that delegates calls to multiple providers
 *
 * This allows running multiple monitoring platforms simultaneously
 */
const createCompositeProvider = (
  providers: MonitoringProvider[],
): MonitoringProvider => ({
  trackError(
    error: Error | string,
    props?: Record<string, string | number>,
    options?: ReportErrorOptions,
  ): void {
    for (const provider of providers) {
      try {
        provider.trackError(error, props, options)
      } catch (err) {
        // Fail silently if a provider throws
        // We don't want one provider failure to break others
      }
    }
  },

  addPageAction(name: string, props?: Record<string, string | number>): void {
    for (const provider of providers) {
      try {
        provider.addPageAction(name, props)
      } catch (err) {
        // Fail silently if a provider throws
      }
    }
  },
})

/**
 * Creates monitoring provider(s) based on configuration
 *
 * @param config - Configuration for monitoring providers
 * @returns A single provider or composite provider if multiple are configured
 */
export function createProvider(
  config: MonitoringConfig = {},
): MonitoringProvider {
  const { providers = ['newrelic'], providerConfig = {} } = config

  // If no providers specified or empty array, use noop
  if (providers.length === 0) {
    return createNoopProvider()
  }

  // Create instances for each configured provider
  const providerInstances: MonitoringProvider[] = []

  for (const providerType of providers) {
    switch (providerType) {
      case 'newrelic':
        if (providerConfig.newrelic?.enabled !== false) {
          providerInstances.push(createNewRelicProvider())
        }
        break

      case 'noop':
        providerInstances.push(createNoopProvider())
        break

      case 'console':
        providerInstances.push(createConsoleProvider())
        break

      default:
        // Unknown provider types are silently ignored
        break
    }
  }

  // If no valid providers were created, use noop
  if (providerInstances.length === 0) {
    return createNoopProvider()
  }

  // If only one provider, return it directly
  if (providerInstances.length === 1) {
    return providerInstances[0]!
  }

  // Multiple providers: return composite
  return createCompositeProvider(providerInstances)
}
